// 店ごとのカード背景画像 (2026-09-26, api migration §44)。
// spec: design/landmark-card-background-spec.md
//
// ファイル選択 → クライアント検証 → 即時アップロード → カード見え方プレビュー。
// 保存 (フォーム submit) とは独立 (= アップロード失敗しても他の項目の編集は続けられる)。
// プレビューは enduser のカードと同じ 2:3 + 上下の暗幕 + 見本の運勢 / 占い文を重ね、
// 「写真のどこが隠れるか」を保存前に確認できるようにする。
import { Button } from '@/components/common/Button';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';

// server 側 (FileService.uploadFile) の許容値と揃える (= クーポン画像と同じ)。
const IMAGE_SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];
const IMAGE_MAX_SIZE = 10 * 1024 * 1024; // 10MB

type Props = {
    value: string | null | undefined,
    onChange: (url: string | null) => void
};

export default observer(function CardBackgroundField({ value, onChange }: Props) {
    const { couponTemplateStore: { uploadImage } } = useStore();
    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // 同一ファイルの再選択でも change を発火させるため毎回クリア。
        e.target.value = '';
        if (!file) return;
        if (!IMAGE_SUPPORTED_FORMATS.includes(file.type)) {
            toastify('error', '画像は jpg / png のみ対応しています');
            return;
        }
        if (file.size > IMAGE_MAX_SIZE) {
            toastify('error', '画像サイズは 10MB 以下にしてください');
            return;
        }
        setUploading(true);
        try {
            const res = await flowResult(uploadImage({ file }));
            if (res?.ok && res.data?.path) {
                onChange(res.data.path);
            } else {
                toastify('error', res?.message || '画像のアップロードに失敗しました');
            }
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className='rounded-md border px-3 py-3'>
            <p className='text-sm font-medium'>カード背景画像 (任意)</p>
            <p className='text-xs text-muted-foreground mt-1'>
                おみくじカードの絵柄を、お店の写真に差し替えます。運勢と占い文は写真の上下に重なります。
                未設定なら従来の絵柄。特典の写真はクーポン編集の「クーポン画像」で設定します (クーポン欄に別に表示)。
            </p>
            <div className='mt-3 flex items-start gap-4'>
                {/* enduser カードと同じ 2:3 の見え方プレビュー */}
                <div className='relative w-[132px] shrink-0 aspect-[2/3] overflow-hidden rounded-md border border-gray-200 bg-gray-100'>
                    {value ?
                        (
                            <>
                                <img src={value} alt='カード背景プレビュー' className='absolute inset-0 w-full h-full object-cover' />
                                <div className='absolute inset-x-0 top-0 h-[30%] bg-gradient-to-b from-black/60 to-transparent' />
                                <div className='absolute inset-x-0 bottom-0 h-[34%] bg-gradient-to-t from-black/70 to-transparent' />
                                <p className='absolute inset-x-0 top-[5%] text-center text-[22px] font-bold text-[#FFD700] drop-shadow'>大吉</p>
                                <p className='absolute inset-x-[6%] bottom-[5%] text-center text-[9px] leading-tight text-white'>
                                    思いがけない出会いが
                                    <br />
                                    幸運を運んできます
                                </p>
                            </>
                        ) :
                        (
                            <p className='absolute inset-0 flex items-center justify-center p-2 text-center text-[11px] text-gray-500'>
                                未設定
                                <br />
                                (従来の絵柄)
                            </p>
                        )}
                </div>
                <div className='flex flex-col gap-2'>
                    <Button
                        type='button'
                        variant='outline'
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                    >
                        {uploading ? 'アップロード中...' : value ? '画像を変更' : '画像を選択'}
                    </Button>
                    {value && (
                        <Button
                            type='button'
                            variant='outline'
                            className='text-red-600'
                            onClick={() => onChange(null)}
                            disabled={uploading}
                        >
                            画像を削除
                        </Button>
                    )}
                    <p className='text-xs text-gray-500'>
                        縦長 (2:3) がおすすめ。上下は運勢と占い文で隠れるので、
                        料理や店の顔は真ん中に。jpg / png、10MB まで。
                    </p>
                </div>
            </div>
            <input
                ref={inputRef}
                type='file'
                accept='image/jpeg,image/png'
                className='hidden'
                onChange={handleFileChange}
            />
        </div>
    );
});
